import React, { useContext, useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import { Fab, Typography } from '@mui/material';
import { LuminateLoader as ProgressLoader } from '@walmart-dataventures/shared-components';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import classNames from 'classnames';
import { colors } from '../../../theme/color-palette';
import { useStyles } from './styles';
import { useSelector, useDispatch } from 'react-redux';
import { UserPortalContext } from '../../../context/appcontext';
import {
	getEditRuleData,
	setRuleType,
	getWatchList,
	setIsCreateRuleOpen,
	updateLastCreatedRule,
	setIsWatchListOpen,
	refreshWatchList,
} from '../../../redux/actions/createRuleActions';
import MenuOption from './MenuOption';
import { Drawer } from '../../../common';
import { Alert, NoData } from '../Alerts';
import { THRESHOLD, BOGLE_LIGHT } from '../../../constants/constant';

const WatchList = ({ onClose }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [showSuccessAlert, setShowSuccessAlert] = useState(false);
	const {
		isWatchListOpen,
		watchListData,
		hasRuleDeleted,
		hasRuleCreated,
		deactivateToggle,
		lastCreatedRuleName,
		watchListLoading,
		editRuleData,
	} = useSelector((state) => state.createRuleReducer);

	const portalUserContext = useContext(UserPortalContext);
	const showNoDataMessage = watchListData?.length === 0;

	useEffect(() => {
		if (hasRuleDeleted || deactivateToggle) {
			dispatch(getWatchList({ portalUserContext }));
			dispatch(refreshWatchList(false));
		}
	}, [hasRuleDeleted, deactivateToggle]);

	useEffect(() => {
		if (lastCreatedRuleName.length) {
			dispatch(getWatchList({ portalUserContext }));
		}
	}, [lastCreatedRuleName]);

	const addRuleHandler = () => {
		dispatch(setRuleType(THRESHOLD));
		dispatch(setIsWatchListOpen(false));
		dispatch(setIsCreateRuleOpen(true));
		dispatch(getEditRuleData(null));
	};

	/**
	 * Close Success Message(Alert) after 2 seconds
	 * Clear last Created Rule name from Store
	 */
	useEffect(() => {
		let timeoutId;
		if (hasRuleCreated && lastCreatedRuleName?.length > 0) {
			setShowSuccessAlert(true);
			timeoutId = setTimeout(() => {
				setShowSuccessAlert(false);
				dispatch(updateLastCreatedRule(''));
			}, 2000);
		}
		return () => clearTimeout(timeoutId);
	}, [hasRuleCreated, lastCreatedRuleName]);

	return (
		<Drawer open={isWatchListOpen} width={360}>
			<Box className={classes.container}>
				<Box className={classes.header}>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<ArrowBackIosNewIcon
							className={classes.backBtn}
							onClick={onClose}
						/>
						<Box className={classes.headerText}>Rules</Box>
					</Box>
					<Fab
						color="primary"
						aria-label="add"
						size="small"
						variant="extended"
						style={{
							backgroundColor: colors.PURPLE,
							fontSize: '14px',
							padding: '10px 20px',
							textTransform: 'none',
						}}
						onClick={addRuleHandler}>
						<AddIcon sx={{ fontSize: '14px' }} />
						Add Rule
					</Fab>
				</Box>
				{/* Loader */}
				{watchListLoading ? <ProgressLoader showImage /> : null}
				{/* Success */}
				{lastCreatedRuleName.length ? (
					<Alert
						success
						open={showSuccessAlert}
						style={{ marginTop: '24px' }}
						content={
							<Fragment>{`Rule ${lastCreatedRuleName} was successfully ${
								editRuleData ? 'edited' : 'added'
							}`}</Fragment>
						}
					/>
				) : null}
				<Box className={classes.content}>
					{/* No Data */}
					{showNoDataMessage ? (
						<NoData
							title={'You don’t have any rules set'}
							content={
								'Create rule to start getting notified when the conditions are met.'
							}
						/>
					) : null}
					<Box>
						{Array.isArray(watchListData) &&
							watchListData?.map((data, idx) => (
								<Box
									className={classNames(classes.flexBox, classes.listStyle)}
									key={`section-${idx}`}>
									<Box sx={{ pl: 1, cursor: 'pointer' }}>
										<Typography
											sx={{
												clear: 'both',
												display: 'block',
												fontSize: '16px',
												wordBreak: 'break-all',
												fontFamily: BOGLE_LIGHT,
												color:
													data.status === 'InActive' ? '#B2B0B9' : ' #333333',
											}}>
											{data.name}
										</Typography>
										<Typography
											sx={{
												display: 'block',
												fontSize: '14px',
												fontFamily: BOGLE_LIGHT,
												color:
													data.status === 'InActive' ? '#B2B0B9' : ' #333333',
											}}>
											{data.left.aggregationLevel}
										</Typography>
									</Box>
									<MenuOption data={data} key={`opt-${idx}`} />
								</Box>
							))}
					</Box>
				</Box>
			</Box>
		</Drawer>
	);
};

WatchList.propTypes = {
	onClose: PropTypes.func,
};

export default WatchList;
